import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/api';
import Cookies from 'js-cookie';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize user from localStorage and verify token on mount
  useEffect(() => {
    const initializeUser = async () => {
      try {
        // First try to get from localStorage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('token') || Cookies.get('token');

        if (storedUser && token) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);

            // Verify token with backend
            const verifyResponse = await authService.verifyToken();
            if (verifyResponse.success) {
              // Update user data with fresh data from backend
              const updatedUser = {
                ...parsedUser,
                ...verifyResponse.data
              };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            } else {
              // Token is invalid, clear everything
              localStorage.removeItem('user');
              localStorage.removeItem('token');
              Cookies.remove('token');
              setUser(null);
            }
          } catch (error) {
            console.error('Error parsing stored user:', error);
            localStorage.removeItem('user');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        Cookies.remove('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, []);

  // Update user data in localStorage when user state changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  const login = async (credentials) => {
    try {
      // Perform login; this stores the token in localStorage/cookies via authService
      const loginResponse = await authService.login(credentials);

      if (loginResponse?.token) {
        // Immediately verify token with backend and use fresh user data
        try {
          const verifyResponse = await authService.verifyToken();
          if (verifyResponse?.success && verifyResponse?.data) {
            const verifiedUser = verifyResponse.data;
            setUser(verifiedUser);
            localStorage.setItem('user', JSON.stringify(verifiedUser));
          }
        } catch (verifyError) {
          // If verify fails, keep minimal state; auth interceptor will handle 401s
          // No rethrow to avoid blocking navigation when login succeeded
        }
      }

      return loginResponse;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    Cookies.remove('token');
  };

  const forgotPassword = async (email) => {
    return await authService.forgotPassword(email);
  };

  const resetPassword = async (data) => {
    return await authService.resetPassword(data);
  };

  const resendOTP = async (email) => {
    return await authService.resendOTP(email);
  };

  const checkPermission = (requiredRoles) => {
    if (!user || !user.role) {
      return false;
    }
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(user.role);
    }
    return user.role === requiredRoles;
  };

  const value = {
    user, loading, login, logout, forgotPassword, resetPassword, resendOTP,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'superAdmin', isPlantAdmin: user?.role === 'plantAdmin',
    isEmployee: user?.role === 'employee',
    checkPermission,
    getToken: () => localStorage.getItem('token') || Cookies.get('token'),
    // Additional user data accessors
    userId: user?._id,
    userName: user?.name,
    userEmail: user?.email,
    userRole: user?.role,
    userPlant: user?.plant,
    userPlantName: user?.plantName,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 